/**
 * v3.followUpTasks — WAVE-12 #10 (Foreign + Deceased) client.
 *
 * Backed by Functions/FollowUpTasksFunction.cs:
 *   GET  /v3/follow-up-tasks?officer=&category=&status=&top=
 *   GET  /v3/follow-up-tasks/by-cif/{cif}
 *   GET  /v3/follow-up-tasks/{id}
 *   POST /v3/follow-up-tasks
 *   PUT  /v3/follow-up-tasks/{id}
 *   POST /v3/follow-up-tasks/{id}/complete
 */

import type {
  RequestOptions,
  FollowUpTask,
  FollowUpTaskCompleteInput,
  FollowUpTaskCreateInput,
  FollowUpTaskListFilters,
  FollowUpTaskListResponse,
  FollowUpTaskUpdateInput,
  FollowUpTaskWriteResponse,
  MyApplicationTasksResponse,
  ReassignFollowUpTasksInput,
  ReassignFollowUpTasksResponse,
  TeamApplicationTasksResponse,
  TeamView,
  TeamViewsResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface FollowUpTaskGetResponse {
  success: boolean;
  task?: FollowUpTask;
  error?: string;
}

export class FollowUpTasksApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: FollowUpTaskListFilters = {},
    opts?: RequestOptions,
  ): Promise<FollowUpTaskListResponse> {
    return this.http.get<FollowUpTaskListResponse>('/v3/follow-up-tasks', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /** Home "My Open Tasks": the officer's open tasks grouped by application, same model as the
   *  "Cart processed" Teams card (Bill 2026-10-16). */
  public myApplications(officer: string, opts?: RequestOptions): Promise<MyApplicationTasksResponse> {
    return this.http.get<MyApplicationTasksResponse>(
      `/v3/follow-up-tasks/my-applications?officer=${encodeURIComponent(officer)}`,
      { ...opts },
    );
  }

  /** Supervisor task view: the caller's team's open tasks (team resolved on the server from the sign-in). */
  public teamApplications(view?: TeamView, officer?: string, opts?: RequestOptions): Promise<TeamApplicationTasksResponse> {
    const query: Record<string, string> = {};
    if (view) query.view = view;
    if (officer) query.officer = officer;
    return this.http.get<TeamApplicationTasksResponse>('/v3/follow-up-tasks/team-applications', {
      ...opts,
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  }

  /** Which of My team / All the caller gets on Home (decided from the sign-in). */
  public teamViews(opts?: RequestOptions): Promise<TeamViewsResponse> {
    return this.http.get<TeamViewsResponse>('/v3/team/views', { ...opts });
  }

  /** Move open tasks to another officer on the caller's team (reason required). */
  public reassign(input: ReassignFollowUpTasksInput, opts?: RequestOptions): Promise<ReassignFollowUpTasksResponse> {
    return this.http.post<ReassignFollowUpTasksResponse>('/v3/follow-up-tasks/reassign', { ...opts, body: input });
  }

  public listByCif(cif: string, opts?: RequestOptions): Promise<FollowUpTaskListResponse> {
    return this.http.get<FollowUpTaskListResponse>(
      `/v3/follow-up-tasks/by-cif/${encodeURIComponent(cif)}`,
      { ...opts },
    );
  }

  public get(id: string, opts?: RequestOptions): Promise<FollowUpTaskGetResponse> {
    return this.http.get<FollowUpTaskGetResponse>(
      `/v3/follow-up-tasks/${encodeURIComponent(id)}`,
      { ...opts },
    );
  }

  public create(
    bypassKey: string,
    body: FollowUpTaskCreateInput,
    opts?: RequestOptions,
  ): Promise<FollowUpTaskWriteResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<FollowUpTaskWriteResponse>('/v3/follow-up-tasks', {
      ...opts,
      headers,
      body,
    });
  }

  public update(
    bypassKey: string,
    id: string,
    body: FollowUpTaskUpdateInput,
    opts?: RequestOptions,
  ): Promise<FollowUpTaskWriteResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.put<FollowUpTaskWriteResponse>(
      `/v3/follow-up-tasks/${encodeURIComponent(id)}`,
      { ...opts, headers, body },
    );
  }

  public complete(
    bypassKey: string,
    id: string,
    body: FollowUpTaskCompleteInput,
    opts?: RequestOptions,
  ): Promise<FollowUpTaskWriteResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<FollowUpTaskWriteResponse>(
      `/v3/follow-up-tasks/${encodeURIComponent(id)}/complete`,
      { ...opts, headers, body },
    );
  }

  /**
   * CHG-0092: re-read Core Director fields 927 (enrolled) / 705 (opted out) for one open
   * "Verify e-statement enrollment" task and close it when Core shows the outcome.
   * result: enrolled | opted-out | mixed (closed) · pending (still open) · skipped.
   */
  public checkEStatement(
    bypassKey: string,
    id: string,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; result?: string; detail?: string; error?: string }> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post(
      `/v3/follow-up-tasks/${encodeURIComponent(id)}/check-estatement`,
      { ...opts, headers, body: {} },
    );
  }
}
